import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Collapse, InputAdornment, TextField, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import {useSnackbar} from 'notistack';
import API_Service from '../../../../Services/API_Service';
import {req_checkin_settings} from '../../../../Helper/Constants';
import {SettingRow, SettingsSaveBar, SettingsSurface, SettingSwitch} from './ui';

const DEFAULT_SETTINGS = {
    image_mandatory: false,
    limit_checkin_radius: false,
    checkin_radius_value: 300,
    checkin_radius_unit: 'm',
    limit_checkout_radius: false,
    checkout_radius_value: 300,
    checkout_radius_unit: 'm'
};

const useStyles = makeStyles((theme) => ({
    skeleton: {marginBottom: theme.spacing(1)},
    radius: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 160px',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(0, 2, 2),
        [theme.breakpoints.down('xs')]: {gridTemplateColumns: '1fr'}
    },
    field: {width: 160, '& .MuiOutlinedInput-root': {borderRadius: 10}, [theme.breakpoints.down('xs')]: {width: '100%'}},
    note: {color: theme.palette.text.secondary, fontSize: 12, lineHeight: 1.5}
}));

const normalize = (data) => ({
    image_mandatory: data.image_mandatory === true || data.image_mandatory === 'true',
    limit_checkin_radius: data.limit_checkin_radius === true || data.limit_checkin_radius === 'true',
    checkin_radius_value: parseFloat(data.checkin_radius_value) || 300,
    checkin_radius_unit: data.checkin_radius_unit || 'm',
    limit_checkout_radius: data.limit_checkout_radius === true || data.limit_checkout_radius === 'true',
    checkout_radius_value: parseFloat(data.checkout_radius_value) || 300,
    checkout_radius_unit: data.checkout_radius_unit || 'm'
});

export default function CheckInSection({axiosConfig, language}) {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const successTimer = useRef(null);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [original, setOriginal] = useState(DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState('idle');

    const load = useCallback(() => {
        setLoading(true);
        API_Service.get(req_checkin_settings, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1 && data.extraData) {
                const loaded = normalize(data.extraData);
                setSettings(loaded);
                setOriginal(loaded);
            } else if (parseInt(data.result) !== 1) {
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    }, [axiosConfig, enqueueSnackbar, language.operation_fail]);

    useEffect(() => {
        load();
        return () => { if (successTimer.current) clearTimeout(successTimer.current); };
    }, [load]);

    const dirty = useMemo(() => JSON.stringify(settings) !== JSON.stringify(original), [settings, original]);
    const toggle = (field) => setSettings((current) => ({...current, [field]: !current[field]}));
    const change = (field, value) => setSettings((current) => ({...current, [field]: value}));

    const save = () => {
        if (settings.limit_checkin_radius && parseFloat(settings.checkin_radius_value) < 300) {
            enqueueSnackbar(language.minimum_radius_note, {variant: 'error'});
            return;
        }
        if (settings.limit_checkout_radius && parseFloat(settings.checkout_radius_value) < 300) {
            enqueueSnackbar(language.minimum_radius_note, {variant: 'error'});
            return;
        }
        setSaveStatus('loading');
        const formdata = new FormData();
        Object.keys(DEFAULT_SETTINGS).forEach((key) => formdata.append(key, settings[key]));
        API_Service.post(req_checkin_settings, formdata, axiosConfig).then(({data}) => {
            if (parseInt(data.result) === 1) {
                setOriginal({...settings});
                setSaveStatus('success');
                enqueueSnackbar(data.message || language.operation_success, {variant: 'success'});
                successTimer.current = setTimeout(() => setSaveStatus('idle'), 1200);
            } else {
                setSaveStatus('error');
                enqueueSnackbar(data.message || language.operation_fail, {variant: 'error'});
            }
        }).catch(() => {
            setSaveStatus('error');
            enqueueSnackbar(language.operation_fail || 'Network error', {variant: 'error'});
        });
    };

    if (loading) {
        return (
            <SettingsSurface>
                {[0, 1, 2].map((row) => <Skeleton className={classes.skeleton} key={row} animation="wave" height={76}/>) }
            </SettingsSurface>
        );
    }

    return (
        <React.Fragment>
            <SettingsSurface>
                <SettingRow title={language.checkin_selfie_mandatory} description={language.checkin_selfie_desc} active={settings.image_mandatory}>
                    <SettingSwitch
                        checked={settings.image_mandatory}
                        onChange={() => toggle('image_mandatory')}
                        inputProps={{'aria-label': language.checkin_selfie_mandatory}}
                    />
                </SettingRow>
                <SettingRow
                    title={language.limit_checkin_radius}
                    description={language.limit_checkin_radius_desc}
                    active={settings.limit_checkin_radius}
                    details={(
                        <Collapse in={settings.limit_checkin_radius} timeout={240} unmountOnExit>
                            <div className={classes.radius} id="checkin-radius-controls">
                                <Typography className={classes.note} id="checkin-radius-note">{language.minimum_radius_note}</Typography>
                                <TextField
                                    className={classes.field}
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={settings.checkin_radius_value}
                                    onChange={(event) => change('checkin_radius_value', event.target.value)}
                                    inputProps={{min: 300, 'aria-label': language.radius_setting, 'aria-describedby': 'checkin-radius-note'}}
                                    InputProps={{endAdornment: <InputAdornment position="end">{settings.checkin_radius_unit}</InputAdornment>}}
                                />
                            </div>
                        </Collapse>
                    )}
                >
                    <SettingSwitch
                        checked={settings.limit_checkin_radius}
                        onChange={() => toggle('limit_checkin_radius')}
                        inputProps={{'aria-label': language.limit_checkin_radius, 'aria-controls': 'checkin-radius-controls', 'aria-expanded': settings.limit_checkin_radius}}
                    />
                </SettingRow>
                <SettingRow
                    title={language.limit_checkout_radius}
                    description={language.limit_checkout_radius_desc}
                    active={settings.limit_checkout_radius}
                    details={(
                        <Collapse in={settings.limit_checkout_radius} timeout={240} unmountOnExit>
                            <div className={classes.radius} id="checkout-radius-controls">
                                <Typography className={classes.note} id="checkout-radius-note">{language.minimum_radius_note}</Typography>
                                <TextField
                                    className={classes.field}
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={settings.checkout_radius_value}
                                    onChange={(event) => change('checkout_radius_value', event.target.value)}
                                    inputProps={{min: 300, 'aria-label': language.radius_setting, 'aria-describedby': 'checkout-radius-note'}}
                                    InputProps={{endAdornment: <InputAdornment position="end">{settings.checkout_radius_unit}</InputAdornment>}}
                                />
                            </div>
                        </Collapse>
                    )}
                >
                    <SettingSwitch
                        checked={settings.limit_checkout_radius}
                        onChange={() => toggle('limit_checkout_radius')}
                        inputProps={{'aria-label': language.limit_checkout_radius, 'aria-controls': 'checkout-radius-controls', 'aria-expanded': settings.limit_checkout_radius}}
                    />
                </SettingRow>
            </SettingsSurface>
            <SettingsSaveBar
                dirty={dirty}
                status={saveStatus}
                onSave={save}
                onReset={() => { setSettings({...original}); setSaveStatus('idle'); }}
                language={language}
            />
        </React.Fragment>
    );
}
